import { useState, useEffect, useCallback, useRef } from 'react';
import { getSocket } from '@/hooks/useRealtimeSync';
import { markUserRecentlyActive } from '@/lib/presenceStore';

const TYPING_EXPIRE_MS = 4000;

/**
 * Список person_id собеседников, которые сейчас набирают текст в треде (событие с сервера с userId).
 */
export function useMessengerTypingPeers(threadId: string | null): string[] {
  const [peers, setPeers] = useState<string[]>([]);
  const timersRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());

  const clearAllTimers = useCallback(() => {
    timersRef.current.forEach((tm) => clearTimeout(tm));
    timersRef.current.clear();
  }, []);

  const clearTimer = useCallback((userId: string) => {
    const t = timersRef.current.get(userId);
    if (t) clearTimeout(t);
    timersRef.current.delete(userId);
  }, []);

  const bumpPeer = useCallback(
    (userId: string) => {
      clearTimer(userId);
      const t = setTimeout(() => {
        timersRef.current.delete(userId);
        setPeers((prev) => prev.filter((id) => id !== userId));
      }, TYPING_EXPIRE_MS);
      timersRef.current.set(userId, t);
      setPeers((prev) => (prev.includes(userId) ? prev : [...prev, userId]));
    },
    [clearTimer]
  );

  useEffect(() => {
    if (!threadId) {
      setPeers([]);
      clearAllTimers();
      return;
    }

    setPeers([]);
    clearAllTimers();

    const socket = getSocket();
    const handler = (data: { threadId?: string; userId?: string }) => {
      if (data?.threadId !== threadId || !data?.userId) return;
      markUserRecentlyActive(data.userId);
      bumpPeer(data.userId);
    };

    socket.on('messenger:peer_typing', handler);
    return () => {
      socket.off('messenger:peer_typing', handler);
      clearAllTimers();
      setPeers([]);
    };
  }, [threadId, bumpPeer, clearAllTimers]);

  return peers;
}
